/**
 * StoragePage — flat list of a project's stores (databases / data stores).
 *
 * Mounted at /projects/:projectId/stores as a child of ProjectLayout (so
 * the sidebar / header / project context come from the layout). Just the
 * list content lives here.
 *
 * Data: GET /datasets/stores → { ok, stores: [{ storeId, name, type,
 * description, schema, status, createdAt }] }. The list is scoped to the
 * caller's project server-side, so no query params are needed.
 *
 * NO create form — stores are created conversationally (ask the agent /
 * Copilot), so the empty state points the user at chat rather than offering
 * a "+ New" button. Each row clicks through to the store DETAIL page
 * (stores/:storeId → StoreDetailPage: viewer, query console, delete).
 *
 * The TABLE itself (columns, chips, row click-through) lives in the shared
 * StoresTable component — the agent Settings → Storage section renders the
 * SAME one over just that agent's stores, so the two surfaces cannot drift
 * apart. This page owns only the header + the fetch.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StoresTable from '../../components/shared/StoresTable/StoresTable';
import { Stores } from '../../services/stores';
// Reuse the pipelines stylesheet for header / wrapper / column widths +
// status-pill colours so this list page looks like a sibling of Apps/Agents.
import '../PipelinesPage/PipelinesPage.css';
import './StoragePage.css';

const StoragePage = () => {
  const { projectId } = useParams();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    Stores.list(projectId)
      .then((data) => {
        if (!alive) return;
        setStores(Array.isArray(data?.stores) ? data.stores : []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || 'Failed to load stores');
        setStores([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  return (
    <>
      <Helmet>
        <title>Storage &amp; Database · Teamy</title>
      </Helmet>

      <div className="pipelines-page">
        {/* The header is unconditional — the page keeps its identity even when
            the list is empty (the empty-state card floating alone on a bare
            page read as a rendering failure). */}
        <div className="pipelines-header">
          <div className="pipelines-header-row">
            <h1 className="pipelines-title">Storage &amp; Database</h1>
          </div>
          <p className="pipelines-subtitle">
            Stores are created conversationally — ask your agent or the Copilot to provision one.
          </p>
        </div>

        {error && !loading ? (
          <div className="store-error" role="alert">
            Could not load stores: {error}
          </div>
        ) : (
          <StoresTable stores={stores} loading={loading} />
        )}
      </div>
    </>
  );
};

export default StoragePage;
